// pages/MedicineRequest/AllMedicineRequests.js
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import UpdateRequestStatusModal from "components/MedicineRequest/UpdateRequestStatusModal";
import { medicineRequestApiService } from "services/medicineRequestApiService";

const AllMedicineRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch all requests
  const fetchRequests = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await medicineRequestApiService.getAllMedicineRequests();
      setRequests(data);
    } catch (error) {
      toast.error("Failed to fetch medicine requests");
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Handle update request status
  const handleUpdateRequest = async (updateData) => {
    if (!selectedRequest) return;

    try {
      await medicineRequestApiService.updateMedicineRequest(selectedRequest.id, updateData);
      toast.success("Request status updated successfully");

      // Update local state
      setRequests(prev =>
        prev.map(request =>
          request.id === selectedRequest.id
            ? {
              ...request,
              responsestatus: updateData.responsestatus,
              additional: updateData.additional || request.additional
            }
            : request
        )
      );

      setUpdateModalOpen(false);
    } catch (error) {
      toast.error("Failed to update request");
      console.error("Error updating request:", error);
      throw error;
    }
  };

  // Handle delete request
  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;

    try {
      await medicineRequestApiService.deleteMedicineRequest(selectedRequest.id);
      toast.success("Request deleted successfully");

      // Remove from local state
      setRequests(prev =>
        prev.filter(request => request.id !== selectedRequest.id)
      );

      setDeleteModalOpen(false);
    } catch (error) {
      toast.error("Failed to delete request");
      console.error("Error deleting request:", error);
      throw error;
    }
  };

  // Handle edit click
  const handleEditClick = (request) => {
    setSelectedRequest(request);
    setUpdateModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (request) => {
    setSelectedRequest(request);
    setDeleteModalOpen(true);
  };

  // Filter requests
  const filteredRequests = useMemo(() => {
    if (!requests.length) return [];

    return requests.filter(request => {
      // Search filter
      const matchesSearch = !searchQuery ||
        (request.medicinename || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (request.manufacture || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (request.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (request.phonenumber || '').includes(searchQuery);

      // Status filter
      const matchesStatus = !statusFilter ||
        (statusFilter === "pending" && !request.responsestatus) ||
        (statusFilter === "responded" && request.responsestatus);

      return matchesSearch && matchesStatus;
    });
  }, [requests, searchQuery, statusFilter]);

  // Format date
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-BD', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    return status ? (
      <span className="badge bg-success">
        <i className="fas fa-check me-1"></i> Responded
      </span>
    ) : (
      <span className="badge bg-warning">
        <i className="fas fa-clock me-1"></i> Pending
      </span>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => `#${row.original.id}`,
      },

      {
        header: "Requested By",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.name || "Anonymous"}</div>
            {row.original.phonenumber && (
              <small className="text-muted">{row.original.phonenumber}</small>
            )}
            {row.original.userid && (
              <small className="d-block text-muted">User ID: {row.original.userid}</small>
            )}
          </div>
        ),
      },
      {
        header: "Medicine",
        accessorKey: "medicinename",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{row.original.medicinename}</div>
            <small className="text-muted">Manufacturer: {row.original.manufacture}</small>
          </div>
        ),
      },
      {
        header: "Additional Info",
        accessorKey: "additional",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="text-truncate" style={{ maxWidth: '200px' }}>
              {row.original.additional || "No additional info"}
            </div>
          </div>
        ),
      },
      {
        header: "Date & Time",
        accessorKey: "datetime",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{formatDateTime(row.original.datetime)}</div>
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "responsestatus",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getStatusBadge(row.original.responsestatus),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              onClick={() => handleEditClick(row.original)}
              className="btn btn-primary btn-sm"
              title="Update Status"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              onClick={() => handleDeleteClick(row.original)}
              className="btn btn-danger btn-sm"
              title="Delete Request"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Statistics
  const totalRequests = requests.length;
  const pendingRequests = requests.filter(r => !r.responsestatus).length;
  const respondedRequests = requests.filter(r => r.responsestatus).length;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Medicine Requests" breadcrumbItem="Manage Requests" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-md-3">
            <div className="card border-primary border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total Requests</p>
                    <h4 className="mt-2">{totalRequests}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-first-aid font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-warning border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Pending</p>
                    <h4 className="mt-2">{pendingRequests}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-clock font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-success border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Responded</p>
                    <h4 className="mt-2">{respondedRequests}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-check-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-info border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Response Rate</p>
                    <h4 className="mt-2">
                      {totalRequests > 0 ? ((respondedRequests / totalRequests) * 100).toFixed(1) : 0}%
                    </h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-chart-line font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Search and Filter Bar */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by medicine name, manufacturer, or requester..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchQuery("")}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="responded">Responded</option>
                </select>
              </div>

              <div className="col-md-2">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success"
                    onClick={() => fetchRequests(false)}
                    disabled={refreshing}
                    title="Refresh Requests"
                  >
                    <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                  </button>
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("");
                    }}
                    disabled={!searchQuery && !statusFilter}
                  >
                    <i className="fas fa-times me-1"></i>
                    Clear
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Requests Table */}
        <div className="card">
          <div className="card-body">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <h4>No medicine requests found</h4>
                <p className="text-muted">
                  {searchQuery || statusFilter
                    ? 'Try adjusting your search filters'
                    : 'No medicine requests available'}
                </p>
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filteredRequests}
                isGlobalFilter={false}
                isPagination={true}
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                isCustomPageSize={true}
              />
            )}
          </div>
        </div>


      </div>

      {/* Modals */}
      <UpdateRequestStatusModal
        isOpen={updateModalOpen}
        toggle={() => setUpdateModalOpen(false)}
        request={selectedRequest}
        onUpdate={handleUpdateRequest}
      />

      <DeleteModal
        show={deleteModalOpen}
        onDeleteClick={handleDeleteRequest}
        loader={false}
        onCloseClick={() => setDeleteModalOpen(false)}
        title="Delete Medicine Request"
        message="Are you sure you want to delete this medicine request? This action cannot be undone."
      />
    </div>
  );
};

export default AllMedicineRequests;